import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldAlert, LogOut } from "lucide-react";

/**
 * Fullscreen blocking overlay shown when a user logs in with a temporary
 * password (must_change_password=true). They cannot proceed until they
 * pick a new password. Logout is the only escape hatch.
 */
export default function ForcePasswordChange() {
    const { refresh, logout } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) { toast.error("A nova palavra-passe deve ter pelo menos 8 caracteres"); return; }
        if (next !== confirm) { toast.error("As palavras-passe não coincidem"); return; }
        if (next === current) { toast.error("A nova palavra-passe tem de ser diferente da atual"); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Palavra-passe atualizada com sucesso. Bem-vindo(a) à plataforma IHTP!");
            // Refresh user context — must_change_password flag now false
            if (refresh) await refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-[#0d1628] flex items-center justify-center p-4 overflow-y-auto" data-testid="force-password-change">
            <div className="bg-white border border-[#b89b6f] max-w-md w-full p-6 md:p-10">
                <div className="flex items-center gap-3 mb-2">
                    <ShieldAlert size={22} className="text-[#b89b6f]" />
                    <div className="label-caps text-[#b89b6f]">Segurança · Primeiro acesso</div>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-[#0d1628]">Defina a sua nova palavra-passe</h2>
                <p className="text-sm text-[#52525b] mt-3">
                    Para sua proteção, é necessário substituir a palavra-passe temporária antes de continuar.
                </p>

                <form onSubmit={submit} className="mt-7 space-y-5" data-testid="fpc-form">
                    <div>
                        <label className="label-caps block mb-2">Palavra-passe temporária</label>
                        <input
                            type={show ? "text" : "password"}
                            required
                            className="input-clean"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            data-testid="fpc-current"
                            autoComplete="current-password"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="label-caps block mb-2">Nova palavra-passe</label>
                        <div className="relative">
                            <input
                                type={show ? "text" : "password"}
                                required
                                minLength={8}
                                className="input-clean pr-10"
                                value={next}
                                onChange={(e) => setNext(e.target.value)}
                                data-testid="fpc-new"
                                autoComplete="new-password"
                            />
                            <button type="button" className="absolute right-2 top-1.5 text-[#52525b]" onClick={() => setShow(!show)} tabIndex={-1}>
                                {show ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="label-caps block mb-2">Confirmar nova palavra-passe</label>
                        <input
                            type={show ? "text" : "password"}
                            required
                            minLength={8}
                            className="input-clean"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            data-testid="fpc-confirm"
                            autoComplete="new-password"
                        />
                    </div>
                    <div className="flex gap-3 pt-2 flex-col sm:flex-row">
                        <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="fpc-submit">
                            {busy ? "A guardar..." : "Definir e entrar"}
                        </button>
                        <button type="button" onClick={logout} className="btn-ghost text-xs sm:text-sm flex items-center justify-center gap-1.5" data-testid="fpc-logout">
                            <LogOut size={12} /> Cancelar e sair
                        </button>
                    </div>
                </form>

                <p className="text-xs text-[#a1a1aa] mt-6">
                    A nova palavra-passe deve ter pelo menos 8 caracteres e ser diferente da temporária.
                </p>
            </div>
        </div>
    );
}
